import type {
  PostingCategoryKind,
  PostingExprNode,
  PostingNode,
} from '@revelio/data-access';

import { findFilterById } from '../../../filters/engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  type TreeFilter,
} from '../../../filters/types/filters';
import {
  buildTreeFilterNodes,
  buildValNode,
  wrapExprNode,
} from './node-builders';
import { PostingDiscoveryTags } from './tags';

const GEOGRAPHY_LEVEL_ID_TO_KIND: Record<string, PostingCategoryKind> = {
  region: PostingDiscoveryTags.Region,
  country: PostingDiscoveryTags.Country,
  state: PostingDiscoveryTags.State,
  county: PostingDiscoveryTags.County,
  msa: PostingDiscoveryTags.Msa,
  city: PostingDiscoveryTags.City,
};

const makeGeographyLeaf = (
  id: number,
  levelId: string
): PostingExprNode | null => {
  const kind = GEOGRAPHY_LEVEL_ID_TO_KIND[levelId];
  return kind ? buildValNode(kind, id) : null;
};

export const buildGeographyFilter = (filters: Filter[]): PostingNode | null => {
  const geographyFilter = findFilterById(filters, FilterId.GEOGRAPHY);
  if (!geographyFilter || geographyFilter.type !== FilterType.TREE) {
    return null;
  }

  const exprNode = buildTreeFilterNodes(geographyFilter, makeGeographyLeaf);
  if (!exprNode) return null;

  return wrapExprNode(exprNode, geographyFilter.isNot);
};

export const buildMultiGeographyFilter = (
  filters: Filter[]
): PostingNode | PostingNode[] | null => {
  const multiGeography = findFilterById(filters, FilterId.MULTI_GEOGRAPHY);
  if (!multiGeography || !isMultiFilter(multiGeography)) {
    return null;
  }
  if (multiGeography.instances.length === 0) {
    return null;
  }

  const instanceNodes = multiGeography.instances
    .filter((inst): inst is TreeFilter => inst?.type === FilterType.TREE)
    .map((instance) => {
      const exprNode = buildTreeFilterNodes(instance, makeGeographyLeaf);
      if (!exprNode) return null;
      return wrapExprNode(exprNode, instance.isNot);
    })
    .filter((node): node is PostingNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }
  return instanceNodes;
};
